// Braking boards: white panels with big black numbers on a post, facing
// oncoming cars. Unlit so they read clearly on a phone at any time of day.

import { Color3, DynamicTexture, Mesh, MeshBuilder, Scene, StandardMaterial } from './babylon';
import { BOARD_DISTANCES, type BoardDistance, type BrakingBoard } from '../track/brakingBoards';

// About 1.6x real size so the numbers are readable on a phone screen in time.
const BOARD_WIDTH = 3.6;
const BOARD_HEIGHT = 2.45;
const BOARD_BOTTOM = 1.0;
/** Angle the face towards the track, like boards set to face the approaching driver. */
const TOE_IN = (15 * Math.PI) / 180;

function boardMaterial(scene: Scene, distance: BoardDistance): StandardMaterial {
  const tex = new DynamicTexture(`board-${distance}`, { width: 512, height: 352 }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  ctx.fillStyle = '#d01010';
  ctx.fillRect(0, 0, 512, 352);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(18, 18, 476, 316);
  ctx.fillStyle = '#111111';
  ctx.font = 'bold 230px Arial, Helvetica, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(distance), 256, 186);
  tex.update(true);
  tex.anisotropicFilteringLevel = 4;

  const mat = new StandardMaterial(`board-${distance}-mat`, scene);
  mat.emissiveTexture = tex;
  mat.disableLighting = true;
  mat.backFaceCulling = true;
  return mat;
}

export function buildBrakingBoards(scene: Scene, boards: BrakingBoard[]): Mesh[] {
  const meshes: Mesh[] = [];
  const postMat = new StandardMaterial('board-post-mat', scene);
  postMat.diffuseColor = new Color3(0.35, 0.36, 0.38);
  const posts: Mesh[] = [];

  for (const d of BOARD_DISTANCES) {
    const mat = boardMaterial(scene, d);
    const panels = boards
      .filter((b) => b.distance === d)
      .map((b, i) => {
        const panel = MeshBuilder.CreatePlane(`board-${d}-${i}`, { width: BOARD_WIDTH, height: BOARD_HEIGHT }, scene);
        panel.position.set(b.x, b.y + BOARD_BOTTOM + BOARD_HEIGHT / 2, b.z);
        // A plane's front faces local -z; rotating by the driving heading turns it towards
        // oncoming cars, and the toe-in turns it a little further towards the track.
        panel.rotation.y = b.heading + (b.side === 'right' ? TOE_IN : -TOE_IN);
        return panel;
      });
    if (panels.length) {
      const merged = Mesh.MergeMeshes(panels, true, true);
      if (merged) {
        merged.material = mat;
        merged.freezeWorldMatrix();
        meshes.push(merged);
      }
    }
  }

  for (const [i, b] of boards.entries()) {
    const post = MeshBuilder.CreateBox(`board-post-${i}`, { width: 0.12, height: BOARD_BOTTOM + 0.1, depth: 0.12 }, scene);
    post.position.set(b.x, b.y + (BOARD_BOTTOM + 0.1) / 2, b.z);
    posts.push(post);
  }
  const mergedPosts = posts.length ? Mesh.MergeMeshes(posts, true, true) : null;
  if (mergedPosts) {
    mergedPosts.material = postMat;
    mergedPosts.freezeWorldMatrix();
    meshes.push(mergedPosts);
  }
  return meshes;
}
